import { useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useRealWeddingReducer } from "../../../Utility";
import { onGetRealWeddings } from "../../../Store/Slices/realWeddingSlice";
import { pages } from "../../../config/appData";
import PageHero from "../../components/PageHero";
import "./RealWeddingsPage.css";

function RealWeddingsPage() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { getListData, getListLoading, getListError } = useRealWeddingReducer();

  useEffect(() => {
    dispatch(onGetRealWeddings());
  }, [dispatch]);

  const list = useMemo(() => {
    const fullList = Array.isArray(getListData) ? getListData : [];
    return fullList.filter((item) => item?.content_type !== "blog");
  }, [getListData]);

  const getCardImage = (wedding) => {
    if (wedding?.cover_photo && typeof wedding.cover_photo === "string" && !wedding.cover_photo.startsWith("C:\\")) {
      return wedding.cover_photo;
    }
    const photos = wedding?.wedding_photos;
    if (Array.isArray(photos) && photos.length > 0) {
      const cover = photos.find((p) => p?.image_type === "cover");
      const url = cover?.image_url ?? photos[0]?.image_url;
      if (url && typeof url === "string" && !url.startsWith("C:\\")) return url;
    }
    return null;
  };

  const getCaption = (item) => {
    const desc = item?.description ?? "";
    const plain = desc.replace(/<[^>]*>/g, "").trim();
    return plain ? plain.slice(0, 120) + (plain.length > 120 ? "…" : "") : "";
  };

  if (getListLoading) {
    return (
      <div className="real-weddings-page content-page">
        <PageHero title={pages.realWeddings.title} subtitle={pages.realWeddings.subtitle} />
        <div className="real-weddings-container content-container">
          <div className="real-weddings-grid real-weddings-loading">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="real-wedding-card-skeleton" aria-hidden="true">
                <div className="real-wedding-card-skeleton-image skeleton-pulse" />
                <div className="real-wedding-card-skeleton-line skeleton-pulse" />
                <div className="real-wedding-card-skeleton-line short skeleton-pulse" />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (getListError) {
    return (
      <div className="real-weddings-page content-page">
        <PageHero
          title={pages.realWeddings.title}
          subtitle={pages.realWeddings.subtitle}
        />
        <div className="real-weddings-container content-container">
          <p className="real-weddings-error">Unable to load content. Please try again later.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="real-weddings-page content-page">
      <PageHero title={pages.realWeddings.title} subtitle={pages.realWeddings.subtitle} />
      <div className="real-weddings-container content-container">
        <div className="real-weddings-grid">
          {list.map((item) => {
            const id = item?.id ?? item?.real_wedding_id;
            const imageUrl = getCardImage(item);
            const heading = item?.heading || "Real Wedding";
            const caption = getCaption(item);
            if (!id) return null;
            return (
              <article
                key={id}
                className="real-wedding-card content-card"
                role="button"
                tabIndex={0}
                onClick={() => navigate(`/real-weddings/${id}`)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    navigate(`/real-weddings/${id}`);
                  }
                }}
                aria-label={`View ${heading}`}
              >
                <div className="real-wedding-card-image-wrapper">
                  {imageUrl ? (
                    <img
                      src={imageUrl}
                      alt={heading}
                      className="real-wedding-card-image"
                      loading="lazy"
                    />
                  ) : (
                    <div className="real-wedding-card-image-placeholder" aria-hidden="true">
                      <span>No image</span>
                    </div>
                  )}
                </div>
                <div className="real-wedding-card-content">
                  <h2 className="real-wedding-card-couple">{heading}</h2>
                  {caption && <p className="real-wedding-card-caption">{caption}</p>}
                </div>
              </article>
            );
          })}
        </div>
        {list.length === 0 && (
          <p className="real-weddings-empty">No real weddings yet. Check back soon.</p>
        )}
      </div>
    </div>
  );
}

export default RealWeddingsPage;
